import { useEffect, useRef, useState } from "react";
import NeuralBackground from "./NeuralBackground";
import { mediaUrl } from "../lib/api";

function MosaicBlock({ side, big, words, sub, fused, eyes }) {
  const isLeft = side === "left";
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  const start = () => {
    timer.current = setInterval(() => setIdx((i) => (i + 1) % words.length), 2000);
  };
  const stop = () => {
    clearInterval(timer.current);
    setIdx((i) => (i + 1) % words.length);
  };
  useEffect(() => () => clearInterval(timer.current), []);

  return (
    <div
      data-testid={`mosaic-block-${side}`}
      onMouseEnter={start}
      onMouseLeave={stop}
      className={`group relative flex-1 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md px-8 py-12 sm:py-16 text-center overflow-hidden
        transition-transform duration-500 ease-out cursor-default
        hover:-translate-y-3 ${isLeft ? "hover:rotate-[-1.5deg]" : "hover:rotate-[1.5deg]"} hover:bg-white/10 hover:shadow-2xl`}
    >
      <div className="relative h-24 sm:h-28 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center gap-3 transition-all duration-500 group-hover:opacity-0 group-hover:-translate-y-4">
          <span className="font-display font-black text-6xl sm:text-8xl text-white tracking-tight">{big}</span>
          {eyes && (
            <span className="flex items-end pb-2 sm:pb-4 gap-1.5">
              <span className="w-3 h-5 sm:w-4 sm:h-6 rounded-full bg-gradient-to-b from-[#7FF4FF] to-[#00CFEF] eye-glow eye-blink" />
              <span className="w-3 h-5 sm:w-4 sm:h-6 rounded-full bg-gradient-to-b from-[#7FF4FF] to-[#00CFEF] eye-glow eye-blink" />
            </span>
          )}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
          <span key={idx} className="font-mono-tech font-bold text-xl sm:text-3xl tracking-[0.18em] uppercase text-[#7FF4FF] word-swap">{words[idx]}</span>
        </div>
      </div>
      <p
        className={`mt-4 font-mono-tech text-xs tracking-[0.3em] uppercase text-[#F8F3E8]/80 transition-all duration-700
          ${fused ? (isLeft ? "translate-x-[30%] opacity-0" : "translate-x-[-30%] opacity-0") : ""}`}
      >
        {sub}
      </p>
    </div>
  );
}

export default function HumanTechMosaic() {
  const secRef = useRef(null);
  const [fused, setFused] = useState(false);
  const [showHands, setShowHands] = useState(false);

  const onMove = (e) => {
    const r = secRef.current.getBoundingClientRect();
    setShowHands((e.clientY - r.top) / r.height > 0.72);
  };

  return (
    <section
      ref={secRef}
      data-testid="human-tech-mosaic"
      onMouseMove={onMove}
      onMouseEnter={() => setFused(true)}
      onMouseLeave={() => { setFused(false); setShowHands(false); }}
      className="relative overflow-hidden bg-gradient-to-br from-[#1F91AE] via-[#15718F] to-[#0E5471]"
    >
      <NeuralBackground density={40} color="190,225,255" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-28">
        <span className="font-mono-tech text-xs tracking-[0.3em] text-[#7FF4FF] block text-center mb-10">
          IA + HUMAN DESIGN // JOIN OUR JOURNEY
        </span>
        <div className="relative flex flex-col lg:flex-row gap-6 lg:gap-16 items-stretch">
          <MosaicBlock side="left" big="IA" sub="Inteligencia Artificial" fused={fused} eyes
            words={["Security", "Reliability", "Robustness", "Bias Mitigation", "Collaboration", "Traceability", "Consistency"]} />
          <MosaicBlock side="right" big="HD" sub="Human Design" fused={fused}
            words={["Guidance", "Oversight", "Control", "Transparency", "Social Impact", "Fairness", "Compliance"]} />
          <div
            data-testid="mosaic-fused-label"
            className={`pointer-events-none absolute left-1/2 bottom-6 lg:bottom-10 -translate-x-1/2 whitespace-nowrap
              font-display font-black text-xl sm:text-3xl tracking-[0.35em] text-[#F8F3E8] transition-all duration-700
              ${fused ? "opacity-100 scale-100" : "opacity-0 scale-75"}`}
          >
            HUMAN&nbsp;TECH
          </div>
        </div>
      </div>
      <div
        data-testid="mosaic-hands-banner"
        className={`absolute bottom-0 left-0 right-0 h-[25%] z-20 transition-transform duration-500 ease-out ${showHands ? "translate-y-0" : "translate-y-full"}`}
      >
        <img src={mediaUrl("/api/uploads/human-tech-hands.jpg")} alt="Mano humana y mano robótica a punto de tocarse"
          className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-transparent to-[#0E5471]/70" />
        <span className="absolute right-6 bottom-3 font-mono-tech text-[10px] tracking-[0.3em] text-[#F8F3E8]/90">
          JOIN THE FUTURE
        </span>
      </div>
    </section>
  );
}
